import { useMemo } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import {
  deleteProduct,
  downloadProductTemplate,
  exportProducts,
  fetchBrands,
  fetchCategories,
  fetchChoiceGroups,
  fetchOptionSets,
  fetchProduct,
  fetchProducts,
  importProducts,
  saveProduct,
  setProductBranches,
  setProductBranchList,
  setProductEnabled,
  setProductPublished,
  validateProductImport,
} from '../api'
import type {
  ProductBranchLinkBody,
  ProductBranchLinkMode,
  ProductListQuery,
  SaveProductBody,
} from '../types'

export const productsKey = ['products'] as const
export const productKey = (id: string) => [...productsKey, 'detail', id] as const
export const categoriesKey = [...productsKey, 'categories'] as const
/**
 * Every brand query, the Brands tab's own list included.
 *
 * Under `products` because a brand's product count moves with every product
 * write — the save, the delete and the import below all invalidate this tree —
 * and that count is what decides whether the brand can be deleted.
 */
export const brandsKey = [...productsKey, 'brands'] as const
export const productBrandsKey = [...brandsKey, 'all'] as const
export const choiceGroupsKey = [...productsKey, 'choice-groups'] as const
export const optionSetsKey = [...productsKey, 'option-sets'] as const

/**
 * One page of the catalogue. The query is part of the key, so paging and filtering
 * each get their own cache entry and stepping back to a page you have already seen
 * is instant.
 */
export function useProducts(query: ProductListQuery) {
  const { data, isPending, isFetching, error, refetch } = useQuery({
    queryKey: [...productsKey, 'list', query],
    queryFn: () => fetchProducts(query),
    // Filters change a keystroke at a time; holding the last page in place stops the
    // table blanking between them.
    placeholderData: (previous) => previous,
  })

  return {
    products: data?.rows ?? [],
    total: data?.count ?? 0,
    isLoading: isPending,
    isRefreshing: isFetching && !isPending,
    error,
    /** Retry after a failure, so a dropped request is not a dead table. */
    retry: () => void refetch(),
  }
}

/** One product, with its variants and choice groups. Idle until an id is given. */
export function useProduct(id: string | null) {
  const { data, isPending, error } = useQuery({
    queryKey: productKey(id ?? ''),
    queryFn: () => fetchProduct(id!),
    enabled: Boolean(id),
  })

  return { product: data ?? null, isLoading: Boolean(id) && isPending, error }
}

/** The tenant's categories, for the filter bar and the form's picker. */
export function useProductCategories() {
  const { data, isPending } = useQuery({ queryKey: categoriesKey, queryFn: fetchCategories })

  return { categories: data ?? [], isLoading: isPending }
}

/**
 * The tenant's brands. `activeBrands` is what the form offers; the list filter
 * takes `brands`, since an inactive brand still sits on products to be found.
 * `isLoaded` is true only once the list has arrived — before that, or after a
 * failure, a brand missing from it says nothing about the brand.
 */
export function useProductBrands() {
  const { data, isPending, isSuccess } = useQuery({
    queryKey: productBrandsKey,
    queryFn: fetchBrands,
  })
  const brands = useMemo(() => data ?? [], [data])
  const activeBrands = useMemo(() => brands.filter((brand) => brand.isActive !== false), [brands])

  return { brands, activeBrands, isLoading: isPending, isLoaded: isSuccess }
}

/**
 * The tenant's saved option axes. Its own key under `products` rather than a reach
 * into `features/modifiers`, which already imports this feature — the extra edge
 * would make the two barrels circular.
 */
export function useOptionSets() {
  const { data, isPending } = useQuery({ queryKey: optionSetsKey, queryFn: fetchOptionSets })

  return { optionSets: data ?? [], isLoading: isPending }
}

export function useChoiceGroups() {
  const { data, isPending } = useQuery({ queryKey: choiceGroupsKey, queryFn: fetchChoiceGroups })

  return { choiceGroups: data ?? [], isLoading: isPending }
}

/**
 * Every write invalidates the whole `products` tree rather than patching a row.
 *
 * Saving a product can move far more than the row that was edited — the server
 * re-slugs it, flashes the cheapest variant's price onto the parent and rewrites the
 * category links — so the list it hands back next is the only reliable picture.
 */
export function useProductActions() {
  const queryClient = useQueryClient()

  function invalidate() {
    return queryClient.invalidateQueries({ queryKey: productsKey })
  }

  const save = useMutation({
    mutationFn: (body: SaveProductBody) => saveProduct(body),
    onSuccess: invalidate,
  })

  const remove = useMutation({
    mutationFn: (id: string) => deleteProduct(id),
    onSuccess: invalidate,
  })

  const toggleEnabled = useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) =>
      setProductEnabled(id, enabled),
    onSuccess: invalidate,
  })

  const togglePublished = useMutation({
    mutationFn: ({ id, published }: { id: string; published: boolean }) =>
      setProductPublished(id, published),
    onSuccess: invalidate,
  })

  // Linking moves the list under an outlet filter, not just the rows' Branches
  // column, so it invalidates the whole tree like every other write here.
  const linkBranches = useMutation({
    mutationFn: ({ mode, body }: { mode: ProductBranchLinkMode; body: ProductBranchLinkBody }) =>
      setProductBranches(mode, body),
    onSuccess: invalidate,
  })

  const setBranches = useMutation({
    mutationFn: ({ id, tenantBranchIds }: { id: string; tenantBranchIds: string[] }) =>
      setProductBranchList(id, tenantBranchIds),
    onSuccess: invalidate,
  })

  return { save, remove, toggleEnabled, togglePublished, linkBranches, setBranches }
}

/**
 * The bulk workbook: two downloads and the two halves of an upload.
 *
 * The import is one mutation driven by a `mode`, not two, so the dialog reads a
 * single `data`/`isPending`/`error` set however far along it is. Checking and
 * then importing the same file otherwise means reconciling two mutations whose
 * results mean the same thing.
 *
 * Only a real import invalidates. A dry run wrote nothing, and refetching the
 * list after one would blank the table for no reason.
 */
export function useProductImport() {
  const queryClient = useQueryClient()

  const downloadTemplate = useMutation({ mutationFn: () => downloadProductTemplate() })

  const exportCatalogue = useMutation({
    mutationFn: (query: ProductListQuery) => exportProducts(query),
  })

  const submit = useMutation({
    mutationFn: ({ file, mode }: { file: File; mode: 'validate' | 'import' }) =>
      mode === 'import' ? importProducts(file) : validateProductImport(file),
    onSuccess: (result, { mode }) => {
      if (mode === 'import' && result.applied) {
        return queryClient.invalidateQueries({ queryKey: productsKey })
      }
    },
  })

  return { downloadTemplate, exportCatalogue, submit }
}
